import { inject, Injectable } from '@angular/core';

import { MemoizedSelector, Store } from '@ngrx/store';
import { defer, delay, finalize, Observable, of, share, switchMap, take, tap, timer } from 'rxjs';

import { ConfigService } from '@fcom/core/services/config/config.service';
import { DapiService } from '@fcom/dapi/api/dapi.service';
import { LowestPriceOfPeriod } from '@fcom/dapi/api/models/lowest-price-of-period';
import { retryWithBackoff } from '@fcom/rx/operators/retryWithBackoff';

import {
  getPricesForPeriod,
  GetPricesForPeriod$Params,
} from '../../../dapi/api/fn/instantsearch/get-prices-for-period';
import {
  getPricesForPeriodWithFixedDepartureDate,
  GetPricesForPeriodWithFixedDepartureDate$Params,
} from '../../../dapi/api/fn/instantsearch/get-prices-for-period-with-fixed-departure-date';
import * as InstantSearchActions from '../../store/actions/instant-search.actions';
import {
  fixedDepartureCacheSelector,
  pricesForPeriodCacheSelector,
} from '../../store/selectors/instant-search.selectors';
import { InstantSearchFeatureState } from '../../store/store.interface';

type CachedResponseSelector = MemoizedSelector<InstantSearchFeatureState, LowestPriceOfPeriod | undefined>;

@Injectable()
export class CommonInstantSearchService {
  private configService = inject(ConfigService);
  private store$ = inject<Store<InstantSearchFeatureState>>(Store);
  private dapiService = inject(DapiService);
  private requestsInFlight = new Map<string, Observable<LowestPriceOfPeriod>>();

  static NUMBER_OF_RETRIES = 2;

  pricesForPeriod(params: GetPricesForPeriod$Params): Observable<LowestPriceOfPeriod> {
    return this.cachedOrFetch(
      `pricesForPeriod:${JSON.stringify(params)}`,
      (currentTime) => pricesForPeriodCacheSelector(params, currentTime),
      defer(() => {
        this.store$.dispatch(InstantSearchActions.pricesForPeriodRequestSent({ params }));
        return this.dapiService
          .invoke$(getPricesForPeriod, this.configService.cfg.instantSearchUrl, params)
          .pipe(retryWithBackoff(CommonInstantSearchService.NUMBER_OF_RETRIES));
      }).pipe(
        tap({
          next: (response) =>
            this.store$.dispatch(
              InstantSearchActions.pricesForPeriodResponseReceived({ params, response, createdAt: Date.now() })
            ),
          error: () => this.store$.dispatch(InstantSearchActions.pricesForPeriodRequestFailed({ params })),
        })
      )
    );
  }

  fixedDeparture(params: GetPricesForPeriodWithFixedDepartureDate$Params): Observable<LowestPriceOfPeriod> {
    return this.cachedOrFetch(
      `fixedDeparture:${JSON.stringify(params)}`,
      (currentTime) => fixedDepartureCacheSelector(params, currentTime),
      defer(() => {
        this.store$.dispatch(InstantSearchActions.fixedDepartureRequestSent({ params }));
        return this.dapiService
          .invoke$(getPricesForPeriodWithFixedDepartureDate, this.configService.cfg.instantSearchUrl, params)
          .pipe(retryWithBackoff(CommonInstantSearchService.NUMBER_OF_RETRIES));
      }).pipe(
        tap({
          next: (response) =>
            this.store$.dispatch(
              InstantSearchActions.fixedDepartureResponseReceived({ params, response, createdAt: Date.now() })
            ),
          error: () => this.store$.dispatch(InstantSearchActions.fixedDepartureRequestFailed({ params })),
        })
      )
    );
  }

  private cachedOrFetch(
    requestKey: string,
    cacheSelector: (currentTime: number) => CachedResponseSelector,
    fetch$: Observable<LowestPriceOfPeriod>
  ): Observable<LowestPriceOfPeriod> {
    return defer(() => this.store$.select(cacheSelector(Date.now()))).pipe(
      take(1),
      switchMap((cached) => (cached ? of(cached).pipe(delay(0)) : this.requestInFlight(requestKey, fetch$)))
    );
  }

  private requestInFlight(
    requestKey: string,
    fetch$: Observable<LowestPriceOfPeriod>
  ): Observable<LowestPriceOfPeriod> {
    if (!this.requestsInFlight.has(requestKey)) {
      this.requestsInFlight.set(
        requestKey,
        fetch$.pipe(
          finalize(() => this.requestsInFlight.delete(requestKey)),
          // Callers switchMap to an identical request, unsubscribing from the old one before subscribing again
          share({ resetOnRefCountZero: () => timer(0) })
        )
      );
    }
    return this.requestsInFlight.get(requestKey);
  }
}
